import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./ReportsPage.css";
import CafeHeader from "../KOTTracker/Header/Header";

const STATUS_OPTIONS = ["ALL", "PENDING", "PREPARING", "READY", "COMPLETED"];

const ReportsPage = () => {
  const [orders, setOrders] = useState([]);
  const [filterRange, setFilterRange] = useState("TODAY");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortOption, setSortOption] = useState("latest");
  const [search, setSearch] = useState("");

  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // ✅ Fetch Orders
  useEffect(() => {
    fetch(
      "https://fliplyn-b-c.fly.dev/orders/cafe/?cafe_id=e7b20008-f754-402a-944c-027bf11d68c0&time_range_minutes=525600"
    )
      .then((res) => res.json())
      .then((data) => setOrders(data))
      .catch((err) => console.log(err));
  }, []);

  // ✅ Date Filter Logic (Wrapped in useCallback)
  const isWithinRange = useCallback(
    (dateString) => {
      const orderDate = new Date(dateString);
      const now = new Date();

      if (filterRange === "TODAY") {
        return orderDate.toDateString() === now.toDateString();
      }

      if (filterRange === "7_DAYS") {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        return orderDate >= sevenDaysAgo && orderDate <= now;
      }

      if (filterRange === "THIS_MONTH") {
        return (
          orderDate.getMonth() === now.getMonth() &&
          orderDate.getFullYear() === now.getFullYear()
        );
      }

      if (filterRange === "CUSTOM" && customStart && customEnd) {
        const start = new Date(customStart);
        const end = new Date(customEnd);
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      }

      return true;
    },
    [filterRange, customStart, customEnd]
  );

  // ✅ Filter + Sort
  const filteredOrders = useMemo(() => {
    let data = [...orders];

    // Date filter
    data = data.filter((o) => isWithinRange(o.created_at));

    // Status filter
    if (statusFilter !== "ALL") {
      data = data.filter((o) => o.order_status === statusFilter);
    }

    // Search filter
    if (search) {
      data = data.filter(
        (o) =>
          o.user_name.toLowerCase().includes(search.toLowerCase()) ||
          o.user_phone.includes(search) ||
          o.table_number.includes(search)
      );
    }

    // Sorting
    if (sortOption === "latest") {
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    if (sortOption === "oldest") {
      data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    if (sortOption === "high") {
      data.sort(
        (a, b) =>
          parseFloat(b.total_amount_to_pay) -
          parseFloat(a.total_amount_to_pay)
      );
    }
    if (sortOption === "low") {
      data.sort(
        (a, b) =>
          parseFloat(a.total_amount_to_pay) -
          parseFloat(b.total_amount_to_pay)
      );
    }

    return data;
  }, [
    orders,
    isWithinRange, // ✅ added
    statusFilter,
    sortOption,
    search
  ]);

  // ✅ Summary Calculations
  const totalOrders = filteredOrders.length;

  const totalRevenue = filteredOrders.reduce(
    (sum, o) => sum + parseFloat(o.total_amount_to_pay || 0),
    0
  );

  const totalGST = filteredOrders.reduce(
    (sum, o) => sum + parseFloat(o.total_gst || 0),
    0
  );

  const avgOrderValue =
    totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0;

  return (
    <>
      <CafeHeader />
      <div className="reports-wrapper">
        {/* FILTER BAR */}
        <div className="filter-bar">
          <div className="filter-left">
            <button
              className={`range-btn ${filterRange === "TODAY" ? "active" : ""}`}
              onClick={() => setFilterRange("TODAY")}
            >
              Today
            </button>

            <button
              className={`range-btn ${filterRange === "7_DAYS" ? "active" : ""}`}
              onClick={() => setFilterRange("7_DAYS")}
            >
              7 Days
            </button>

            <button
              className={`range-btn ${
                filterRange === "THIS_MONTH" ? "active" : ""
              }`}
              onClick={() => setFilterRange("THIS_MONTH")}
            >
              This Month
            </button>

            <button
              className={`range-btn ${filterRange === "CUSTOM" ? "active" : ""}`}
              onClick={() => setFilterRange("CUSTOM")}
            >
              Custom
            </button>

            {filterRange === "CUSTOM" && (
              <div className="custom-date">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="filter-right">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="latest">Sort by Latest</option>
              <option value="oldest">Sort by Oldest</option>
              <option value="high">Highest Amount</option>
              <option value="low">Lowest Amount</option>
            </select>

            <input
              type="text"
              placeholder="Search name, phone, table..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* SUMMARY */}
        <div className="summary-grid">
          <div className="summary-card">
            <h4>Total Orders</h4>
            <h2>{totalOrders}</h2>
          </div>

          <div className="summary-card">
            <h4>Total Revenue</h4>
            <h2>₹{totalRevenue.toLocaleString()}</h2>
          </div>

          <div className="summary-card">
            <h4>GST Collected</h4>
            <h2>₹{totalGST.toLocaleString()}</h2>
          </div>

          <div className="summary-card">
            <h4>Avg Order Value</h4>
            <h2>₹{avgOrderValue}</h2>
          </div>
        </div>

        {/* TABLE */}
        <div className="report-table">
          <table>
            <thead>
              <tr>
                <th>ORDER ID</th>
                <th>DATE & TIME</th>
                <th>TABLE</th>
                <th>CUSTOMER</th>
                <th>GUESTS</th>
                <th>STATUS</th>
                <th>NET AMT</th>
                <th>GST</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>{order.id.slice(0, 6).toUpperCase()}</td>
                  <td>{new Date(order.created_at).toLocaleString()}</td>
                  <td>{order.table_number}</td>
                  <td>{order.user_name}</td>
                  <td>{order.number_of_guests}</td>
                  <td>
                    <span className={`status ${order.order_status}`}>
                      {order.order_status}
                    </span>
                  </td>
                  <td>₹{order.net_amount}</td>
                  <td>₹{order.total_gst}</td>
                  <td>₹{order.total_amount_to_pay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ReportsPage;